import { createPortal } from "react-dom";

import type { ElationPatient } from "~/bridge/types";

function patientName(p: ElationPatient): string {
  return (
    p.patientName?.trim() ||
    [p.firstName ?? "", p.lastName ?? ""].join(" ").trim() ||
    "this patient"
  );
}

/**
 * Prompt shown when a *different* Elation patient chart is detected while
 * viewing a matched patient. Rendering is driven entirely by `candidate`:
 * pass the detected patient to open it, `null` to close it (so it auto-hides
 * if the detection reverts before the user chooses).
 */
export function SwitchPatientModal({
  candidate,
  onStay,
  onNavigate,
}: {
  candidate: ElationPatient | null;
  onStay: () => void;
  onNavigate: () => void;
}) {
  if (!candidate) return null;
  const name = patientName(candidate);
  // Portal to <body> so the fixed overlay is a top-level sibling of #root.
  // Rendering a full-viewport fixed overlay deep in the profile tree can sit
  // under an ancestor that establishes a containing block / compositing layer
  // (transform, filter, contain), which re-rasterizes — and visibly degrades —
  // the background text. Portaling escapes that.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Switch patient"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onStay} />
      <div className="relative w-full max-w-sm rounded-2xl border border-border bg-card text-card-foreground shadow-xl p-6">
        <h2 className="text-base font-semibold text-foreground mt-0 mb-1">
          New patient detected in Elation
        </h2>
        <p className="text-sm text-muted-foreground mt-0 mb-5">
          Elation is now showing{" "}
          <span className="font-medium text-foreground">{name}</span>, who is
          different from the patient you&apos;re viewing. Switch to the member
          matching flow for them?
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onStay}
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-md"
          >
            Stay on current patient
          </button>
          <button
            type="button"
            onClick={onNavigate}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            Navigate to {name}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
